/* ============================================
   Configuración → Equipo

   Quién más entra a esta cuenta y a qué. El nivel es lo único que decide: un
   colaborador trabaja el CRM y no ve lo que la empresa cobra; el dueño y los
   administradores además reparten accesos.

   El alta invita por correo: la persona recibe un enlace que vence y crea
   ahí su propia contraseña. Ninguna contraseña viaja por correo ni por
   WhatsApp, y nadie de esta cuenta llega a verla.

   El enlace también se muestra acá, y no es redundancia: el correo puede
   caer en el spam del dominio de la persona, y sin el enlace a mano la única
   salida sería pedírnoslo a nosotros.
   ============================================ */
import { useCallback, useEffect, useState } from 'react'
import { useApi } from '@/hooks/useApi'
import { showToast } from '@/components/layout/Toast'
import { Badge } from '@/components/ui'
import type { BadgeVariant } from '@/components/ui'
import { IconCheck, IconCopy, IconTrash, IconUser, IconX } from '@/components/icons/Icons'

type Nivel = 'dueno' | 'administrador' | 'contabilidad' | 'colaborador'

interface Miembro {
  usuario_id: number
  email: string
  nombre: string | null
  foto_url: string | null
  nivel: Nivel
  debe_cambiar_clave: boolean
  desde: string | null
}

interface Respuesta {
  equipo: Miembro[]
  puede_gestionar: boolean
  yo: number
}

/* Qué significa cada nivel, en una línea. Los nombres solos no dicen nada:
   «contabilidad» y «colaborador» se diferencian en algo concreto. */
const NIVEL: Record<Nivel, { label: string; variant: BadgeVariant; que: string }> = {
  dueno: {
    label: 'Dueño',
    variant: 'aceptada',
    que: 'Creó la cuenta. Ve todo y reparte los accesos.',
  },
  administrador: {
    label: 'Administrador',
    variant: 'enviada',
    que: 'Ve todo y puede sumar o quitar gente del equipo.',
  },
  contabilidad: {
    label: 'Contabilidad',
    variant: 'warning',
    que: 'Ve las cotizaciones y las facturas. No reparte accesos.',
  },
  colaborador: {
    label: 'Colaborador',
    variant: 'neutral',
    que: 'Trabaja el CRM. No ve cotizaciones ni facturas.',
  },
}

const ASIGNABLES: Nivel[] = ['administrador', 'contabilidad', 'colaborador']

function iniciales(nombre: string | null, email: string): string {
  const partes = (nombre || '').trim().split(/\s+/).filter(Boolean)
  if (partes.length >= 2) return (partes[0][0] + partes[1][0]).toUpperCase()
  if (partes.length === 1 && partes[0].length > 1) return partes[0].slice(0, 2).toUpperCase()
  return (email[0] || '?').toUpperCase()
}

function desdeCuando(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('es-PE', { day: '2-digit', month: 'short', year: 'numeric' })
}

export default function PerfilEquipo() {
  const api = useApi()
  const [datos, setDatos] = useState<Respuesta | null>(null)
  const [cargando, setCargando] = useState(true)

  const [sumando, setSumando] = useState(false)
  const [nombre, setNombre] = useState('')
  const [email, setEmail] = useState('')
  const [nivel, setNivel] = useState<Nivel>('colaborador')
  const [guardando, setGuardando] = useState(false)
  const [error, setError] = useState<string | null>(null)

  /* La invitación recién mandada: a quién fue, si el correo salió y el enlace
     de respaldo. Vive en pantalla hasta que la cierren. */
  const [entrega, setEntrega] = useState<
    { email: string; enlace: string | null; enviado: boolean; mensaje: string } | null
  >(null)
  const [copiado, setCopiado] = useState(false)

  const cargar = useCallback(() => {
    setCargando(true)
    api.get<Respuesta>('/api/portal/equipo')
      .then(setDatos)
      .catch(() => showToast('No se pudo leer el equipo', 'error'))
      .finally(() => setCargando(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => { cargar() }, [cargar])

  async function sumar() {
    if (!nombre.trim()) { setError('Falta el nombre'); return }
    if (!email.trim()) { setError('Falta el correo'); return }
    setGuardando(true)
    setError(null)
    try {
      const r = await api.post<{ email: string; enlace: string | null; enviado: boolean; mensaje: string }>(
        '/api/portal/equipo', { nombre: nombre.trim(), email: email.trim(), nivel },
      )
      setEntrega({ email: r.email, enlace: r.enlace, enviado: r.enviado, mensaje: r.mensaje })
      setSumando(false)
      setNombre(''); setEmail(''); setNivel('colaborador')
      cargar()
    } catch (err: any) {
      setError(err?.message || 'No se pudo sumar a esa persona')
    } finally { setGuardando(false) }
  }

  async function cambiarNivel(m: Miembro, nuevo: Nivel) {
    try {
      const r = await api.put<{ aviso?: string }>(`/api/portal/equipo/${m.usuario_id}`, { nivel: nuevo })
      showToast(r?.aviso || 'Nivel actualizado', 'success')
      cargar()
    } catch (err: any) {
      showToast(err?.message || 'No se pudo cambiar el nivel', 'error')
    }
  }

  async function reenviar(m: Miembro) {
    try {
      const r = await api.post<{ email: string; enlace: string; enviado: boolean; mensaje: string }>(
        `/api/portal/equipo/${m.usuario_id}/invitacion`, {},
      )
      setEntrega({ email: r.email, enlace: r.enlace, enviado: r.enviado, mensaje: r.mensaje })
    } catch (err: any) {
      showToast(err?.message || 'No se pudo reenviar la invitación', 'error')
    }
  }

  async function quitar(m: Miembro) {
    if (!window.confirm(`¿Quitar a ${m.nombre || m.email} de la cuenta? Deja de poder entrar.`)) return
    try {
      await api.del(`/api/portal/equipo/${m.usuario_id}`)
      showToast('Listo, ya no tiene acceso', 'success')
      cargar()
    } catch (err: any) {
      showToast(err?.message || 'No se pudo quitar a esa persona', 'error')
    }
  }

  function copiar(texto: string) {
    navigator.clipboard.writeText(texto)
      .then(() => { setCopiado(true); window.setTimeout(() => setCopiado(false), 1800) })
      .catch(() => showToast('No se pudo copiar', 'error'))
  }

  if (cargando && !datos) return <p className="pp-cargando">Cargando…</p>

  const equipo = datos?.equipo ?? []
  const puede = !!datos?.puede_gestionar

  return (
    <>
      <div className="card pp-card">
        <div className="pp-card__head">
          <div>
            <h3 className="pp-card__titulo">Tu equipo</h3>
            <p className="pp-card__sub">
              Quién más entra a esta cuenta. El nivel decide qué ve cada uno, y
              al sumar a alguien le llega por correo un enlace para crear su contraseña.
              {!puede && ' Solo el dueño o un administrador pueden cambiarlo.'}
            </p>
          </div>
          {puede && !sumando && (
            <button type="button" className="btn btn-sm btn-primary" onClick={() => { setSumando(true); setError(null) }}>
              <IconUser size={14} /> Sumar a alguien
            </button>
          )}
        </div>

        {/* La invitación recién mandada. Se queda hasta que la cierren: si el
            correo no salió, ese enlace es lo único que hay que copiar antes de
            salir de la pantalla. */}
        {entrega && (
          <div className={`pp-entrega${entrega.enlace && !entrega.enviado ? ' pp-entrega--atencion' : ''}`}>
            <div className="pp-entrega__texto">
              <strong>{entrega.email}</strong>
              <span>{entrega.mensaje}</span>
              {entrega.enlace && (
                <code className="pp-entrega__enlace">{entrega.enlace}</code>
              )}
            </div>
            <div className="pp-entrega__acciones">
              {entrega.enlace && (
                <button type="button" className="btn btn-sm btn-secondary" onClick={() => copiar(entrega.enlace!)}>
                  {copiado ? <><IconCheck size={13} /> Copiado</> : <><IconCopy size={13} /> Copiar enlace</>}
                </button>
              )}
              <button type="button" className="btn btn-sm btn-secondary" onClick={() => setEntrega(null)}>
                <IconX size={13} /> Cerrar
              </button>
            </div>
          </div>
        )}

        {sumando && (
          <div className="pp-alta">
            <div className="pp-grid-campos pp-alta__campos">
              <div className="form-group">
                <label className="form-label">Nombre y apellido</label>
                <input className="form-input" value={nombre} onChange={e => setNombre(e.target.value)} placeholder="Cómo se llama" autoFocus />
              </div>
              <div className="form-group">
                <label className="form-label">Correo</label>
                <input className="form-input" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="nombre@empresa.com" />
              </div>
              <div className="form-group">
                <label className="form-label">Nivel</label>
                <select className="form-input" value={nivel} onChange={e => setNivel(e.target.value as Nivel)}>
                  {ASIGNABLES.map(n => <option key={n} value={n}>{NIVEL[n].label}</option>)}
                </select>
              </div>
              <p className="pp-nivel-ayuda pp-ancho">{NIVEL[nivel].que}</p>
            </div>
            {error && <p className="pp-aviso pp-aviso--error">{error}</p>}
            <div className="pp-alta__acciones">
              <button type="button" className="btn btn-secondary" onClick={() => setSumando(false)} disabled={guardando}>
                Cancelar
              </button>
              <button type="button" className="btn btn-primary" onClick={sumar} disabled={guardando}>
                {guardando ? 'Sumando…' : 'Sumar al equipo'}
              </button>
            </div>
          </div>
        )}

        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Persona</th>
                <th>Nivel</th>
                <th>Desde</th>
                <th>Estado</th>
                {puede && <th />}
              </tr>
            </thead>
            <tbody>
              {equipo.map(m => {
                const meta = NIVEL[m.nivel] ?? NIVEL.colaborador
                const soyYo = m.usuario_id === datos?.yo
                const editable = puede && !soyYo && m.nivel !== 'dueno'
                return (
                  <tr key={m.usuario_id}>
                    <td>
                      <div className="pp-equipo__persona">
                        <span className="pp-equipo__avatar">
                          {m.foto_url ? <img src={m.foto_url} alt="" /> : iniciales(m.nombre, m.email)}
                        </span>
                        <div className="cell-stack">
                          <span className="cell-stack__title">
                            {m.nombre || m.email}
                            {soyYo && <span className="pp-equipo__yo">tú</span>}
                          </span>
                          <span className="cell-stack__sub">
                            {m.nombre ? m.email : 'todavía no puso su nombre'}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td>
                      {editable ? (
                        <select
                          className="form-input pp-equipo__select"
                          value={m.nivel}
                          onChange={e => cambiarNivel(m, e.target.value as Nivel)}
                          title={meta.que}
                        >
                          {ASIGNABLES.map(n => <option key={n} value={n}>{NIVEL[n].label}</option>)}
                        </select>
                      ) : (
                        <span title={meta.que}><Badge variant={meta.variant}>{meta.label}</Badge></span>
                      )}
                    </td>
                    <td className="pp-equipo__fecha">{desdeCuando(m.desde)}</td>
                    <td>
                      {m.debe_cambiar_clave
                        ? <Badge variant="warning">Invitado</Badge>
                        : <span className="pp-equipo__fecha">Activo</span>}
                    </td>
                    {puede && (
                      <td>
                        {editable && (
                          <div className="row-actions row-actions--nowrap">
                            <button type="button" className="btn btn-sm btn-secondary" onClick={() => reenviar(m)} title="Mandarle otra vez el enlace para entrar">
                              Reenviar
                            </button>
                            <button type="button" className="btn btn-sm btn-danger" onClick={() => quitar(m)} title="Quitar de la cuenta">
                              <IconTrash size={13} />
                            </button>
                          </div>
                        )}
                      </td>
                    )}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Qué puede cada nivel, escrito. Sin esto los nombres son decoración y
          nadie sabe a quién poner en cuál. */}
      <div className="card pp-card">
        <div className="pp-card__head">
          <div>
            <h3 className="pp-card__titulo">Qué puede cada nivel</h3>
            <p className="pp-card__sub">
              Todos ven y trabajan la cartera de la empresa: empresas, contactos,
              oportunidades y tareas. La diferencia está en lo demás.
            </p>
          </div>
        </div>
        <ul className="pp-niveles">
          {(Object.keys(NIVEL) as Nivel[]).map(n => (
            <li key={n}>
              <Badge variant={NIVEL[n].variant}>{NIVEL[n].label}</Badge>
              <span>{NIVEL[n].que}</span>
            </li>
          ))}
        </ul>
      </div>
    </>
  )
}
